// ✅ PHASE 4: Push Notifications Hook
// According to PROFESSIONAL_REFACTORING_PLAN.md lines 496-499
// Manages FCM token registration and foreground notification handling

import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import {
  initializeFirebase,
  requestNotificationPermission,
  setupForegroundMessageHandler,
  isFCMAvailable
} from '@/config/firebase'
import { apiClient } from '@/services/apiClient'
import { logger } from '@/utils/logger'

interface UsePushNotificationsReturn {
  isSupported: boolean
  isPermissionGranted: boolean
  fcmToken: string | null
  requestPermission: () => Promise<void>
  error: string | null
}

/**
 * Hook for managing push notifications
 * - Automatically requests permission for couriers on mount
 * - Registers FCM token with backend
 * - Handles foreground notifications
 */
export const usePushNotifications = (): UsePushNotificationsReturn => {
  const { user } = useAuth()
  const [isSupported, setIsSupported] = useState(false)
  const [isPermissionGranted, setIsPermissionGranted] = useState(false)
  const [fcmToken, setFcmToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  /**
   * Send FCM token to backend for storage
   */
  const registerTokenWithBackend = useCallback(async (token: string) => {
    if (!user?.id) {
      logger.warn('Cannot register FCM token: user not authenticated')
      return
    }

    try {
      await apiClient.post('/push-tokens', {
        token,
        userId: user.id,
        deviceType: 'web',
        userAgent: navigator.userAgent,
      })

      logger.info('FCM token registered with backend', {
        action: 'fcm_token_registered',
        metadata: { userId: user.id }
      })
    } catch (error) {
      logger.error('Failed to register FCM token with backend', {
        action: 'fcm_token_register_error',
        error: error instanceof Error ? error.message : error,
        metadata: { userId: user.id }
      })
      throw error
    }
  }, [user?.id])

  /**
   * Request notification permission and get FCM token
   */
  const requestPermission = useCallback(async () => {
    try {
      setError(null)

      // Initialize Firebase first
      initializeFirebase()

      // Request permission and get token
      const token = await requestNotificationPermission()

      if (!token) {
        setError('Failed to get FCM token')
        return
      }

      setFcmToken(token)
      setIsPermissionGranted(true)

      // Register with backend
      await registerTokenWithBackend(token)

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
      logger.error('Failed to request notification permission', {
        action: 'notification_permission_error',
        error: message
      })
    }
  }, [registerTokenWithBackend])

  /**
   * Handle foreground notifications
   */
  const handleForegroundMessage = useCallback((payload: any) => {
    logger.info('Foreground notification received', {
      action: 'fcm_foreground_message',
      metadata: {
        title: payload.notification?.title,
        body: payload.notification?.body,
        orderId: payload.data?.orderId
      }
    })

    // Show browser notification (custom UI)
    if (payload.notification) {
      new Notification(payload.notification.title || 'Agciceyim Admin', {
        body: payload.notification.body,
        icon: payload.notification.icon || '/icon-192.png',
        badge: '/icon-192.png',
        tag: payload.data?.orderId || 'general',
        requireInteraction: payload.data?.priority === 'high',
        data: payload.data,
      })
    }

    // TODO: Show in-app notification toast
    // Could use sonner toast here for better UX
  }, [])

  /**
   * Initialize Firebase and check support on mount
   */
  useEffect(() => {
    const supported = isFCMAvailable()
    setIsSupported(supported)

    if (!supported) {
      logger.info('FCM not available or not configured')
      return
    }

    // Check current permission status
    if ('Notification' in window) {
      setIsPermissionGranted(Notification.permission === 'granted')
    }
  }, [])

  /**
   * Auto-request push permission for staff roles on mount.
   * Admins, support, dispatchers and couriers all need real-time order push.
   */
  useEffect(() => {
    if (!isSupported || !user) return

    const staffRoles = ['admin', 'courier']
    if (!staffRoles.includes(user.role)) {
      logger.info('User role does not need auto push', {
        action: 'push_auto_skip',
        metadata: { role: user.role }
      })
      return
    }

    // Skip if already granted
    if (isPermissionGranted && fcmToken) {
      logger.info('Push notifications already enabled', {
        action: 'push_already_enabled',
        metadata: { userId: user.id }
      })
      return
    }

    logger.info('Auto-requesting push notifications for staff', {
      action: 'push_auto_request',
      metadata: { userId: user.id, role: user.role }
    })

    requestPermission()
  }, [isSupported, user, isPermissionGranted, fcmToken, requestPermission])

  /**
   * Set up foreground message handler
   */
  useEffect(() => {
    if (!isSupported || !isPermissionGranted) return

    const unsubscribe = setupForegroundMessageHandler(handleForegroundMessage)

    return () => {
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [isSupported, isPermissionGranted, handleForegroundMessage])

  /**
   * Send Firebase config to Service Worker on mount
   */
  useEffect(() => {
    if (!isSupported) return

    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      // Send config to firebase-messaging-sw.js
      navigator.serviceWorker.controller.postMessage({
        type: 'FIREBASE_CONFIG',
        config: {
          apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
          authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
          projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
          storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
          messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
          appId: import.meta.env.VITE_FIREBASE_APP_ID,
        }
      })

      logger.info('Firebase config sent to Service Worker', {
        action: 'fcm_sw_config_sent'
      })
    }
  }, [isSupported])

  return {
    isSupported,
    isPermissionGranted,
    fcmToken,
    requestPermission,
    error,
  }
}

